import React from 'react';
import FormInput from '../../../../../components/FormInput';
import SearchableSelect from '../../../../../components/SearchableSelect';
import { RESOURCE_TIMEZONES } from '../../../../../utils/timezones';

// Must mirror the backend `MeasurementUnits` choices
// (api/users/models/user_resources.py).
const MEASUREMENT_UNITS = [
  { value: 'w', label: 'Watt (W)' },
  { value: 'kw', label: 'Kilowatt (kW)' },
  { value: 'mw', label: 'Megawatt (MW)' },
  { value: 'mwh', label: 'Megawatt-hour (MWh)' },
  { value: 'eur_mwh', label: 'EUR/MWh' },
];

export default function AddEditResourceForm({form, onChange, onSubmit, editing, ownerOptions = [], isEnabled, onCancel, nameRef}) {
  // Ensure the current owner is selectable even if it is not a market owner
  // (e.g. a legacy resource still owned by the superuser creator).
  const owners = (() => {
    const opts = [...ownerOptions];
    if (editing && editing.user && !opts.some(o => String(o.value) === String(editing.user))) {
      opts.unshift({ value: editing.user, label: editing.email || editing.user });
    }
    return opts;
  })();
  return (
    <div className="p-6">
      <div className="flex items-start gap-2.5 mb-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {editing ? (
            <span className="text-brand-600 dark:text-brand-400">{editing.name || 'Edit Resource'}</span>
          ) : (
            'Add Resource'
          )}
        </h3>
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Name</label>
          <FormInput
            ref={nameRef}
            size="medium"
            value={form.name}
            onChange={e => onChange({...form, name: e.target.value})}
            required
            placeholder="e.g. Wind Farm 1"
            disabled={!isEnabled}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Timezone</label>
          <SearchableSelect
            value={form.timezone || ''}
            onChange={(val) => onChange({...form, timezone: val})}
            options={RESOURCE_TIMEZONES.map(t => ({ value: t, label: t }))}
            placeholder="Select timezone"
            disabled={!isEnabled}
            className="w-full"
            showCount={false}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Units</label>
          <select
            value={form.units || 'mw'}
            onChange={e => onChange({...form, units: e.target.value})}
            disabled={!isEnabled}
            className={`block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
              !isEnabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {MEASUREMENT_UNITS.map(u => (
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Use case</label>
          <select
            value={form.use_case}
            onChange={e => onChange({...form, use_case: e.target.value})}
            disabled={!isEnabled}
            className={`block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
              !isEnabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <option value="">Select</option>
            <option value="solar_power">Solar Power</option>
            <option value="wind_power">Wind Power</option>
            <option value="mfrr_price">mFRR Price</option>
            <option value="mfrr_energy">mFRR Energy</option>
          </select>
        </div>

        {/* Ensemble forecast floor: only meaningful on edit (the API is
            superuser-only and ignores it on create; new resources start
            clipped at 0). Toggle off = null = signed series, no clipping. */}
        {editing && (
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Clip ensemble forecasts at a minimum
              </label>
              <button
                type="button"
                onClick={() => onChange({...form, forecast_floor_enabled: !form.forecast_floor_enabled})}
                disabled={!isEnabled}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                  form.forecast_floor_enabled ? 'bg-brand-600' : 'bg-gray-300 dark:bg-gray-600'
                } ${!isEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.forecast_floor_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            {form.forecast_floor_enabled ? (
              <div className="mt-2">
                <FormInput
                  size="medium"
                  type="number"
                  step="any"
                  value={form.forecast_floor}
                  onChange={e => onChange({...form, forecast_floor: e.target.value})}
                  placeholder="0"
                  disabled={!isEnabled}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Ensemble forecasts never go below this value. 0 suits
                  non-negative targets (wind, solar generation).
                </p>
              </div>
            ) : (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                No floor: use for signed targets that can go negative
                (e.g. net mFRR energy).
              </p>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Owner</label>
          <SearchableSelect
            value={form.user || ''}
            onChange={(val) => onChange({...form, user: val})}
            options={owners}
            placeholder="Select market owner (defaults to you)"
            disabled={!isEnabled}
            className="w-full"
            showCount={false}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Assign this resource to an existing market owner. Leave empty to own it yourself.
          </p>
        </div>

        {/* Public market profile: rendered behind the "Details" disclosure on
            the /markets directory. Blank target = no disclosure on the row. */}
        <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Public market profile
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Forecast target (one sentence)
            </label>
            <textarea
              rows={2}
              maxLength={280}
              value={form.target_summary || ''}
              onChange={e => onChange({...form, target_summary: e.target.value})}
              disabled={!isEnabled}
              placeholder="Total solar generation on the Belgian grid, as one national number."
              className={`block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
                !isEnabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Shown publicly on the markets directory. Leave empty to hide the
              details panel for this resource.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Target note (optional)
            </label>
            <textarea
              rows={2}
              maxLength={280}
              value={form.target_note || ''}
              onChange={e => onChange({...form, target_note: e.target.value})}
              disabled={!isEnabled}
              placeholder="A caveat or benchmark a forecaster should know up front."
              className={`block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
                !isEnabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Measurement source
            </label>
            <FormInput
              size="medium"
              value={form.source_name || ''}
              onChange={e => onChange({...form, source_name: e.target.value})}
              placeholder="e.g. Elia Open Data"
              disabled={!isEnabled}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Measurement source URL
            </label>
            <FormInput
              size="medium"
              type="url"
              value={form.source_url || ''}
              onChange={e => onChange({...form, source_url: e.target.value})}
              placeholder="https://opendata.elia.be/explore/dataset/ods032/"
              disabled={!isEnabled}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                Resolution (minutes)
              </label>
              <FormInput
                size="medium"
                type="number"
                step="1"
                min="1"
                value={form.resolution_minutes ?? 15}
                onChange={e => onChange({...form, resolution_minutes: e.target.value})}
                placeholder="15"
                disabled={!isEnabled}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                Program page slug
              </label>
              <FormInput
                size="medium"
                value={form.program_slug || ''}
                onChange={e => onChange({...form, program_slug: e.target.value})}
                placeholder="elia"
                disabled={!isEnabled}
              />
            </div>
          </div>
        </div>

        <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Intraday budget (€/month)
            </label>
            <FormInput
              size="medium"
              type="number"
              step="0.01"
              min="0"
              value={form.monthly_budget_intraday}
              onChange={e => onChange({...form, monthly_budget_intraday: e.target.value})}
              placeholder="3500.00"
              disabled={!isEnabled}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Day-Ahead (D+1) budget (€/month)
            </label>
            <FormInput
              size="medium"
              type="number"
              step="0.01"
              min="0"
              value={form.monthly_budget_d1}
              onChange={e => onChange({...form, monthly_budget_d1: e.target.value})}
              placeholder="3500.00"
              disabled={!isEnabled}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              D+N budget (€/month)
            </label>
            <FormInput
              size="medium"
              type="number"
              step="0.01"
              min="0"
              value={form.monthly_budget_dn}
              onChange={e => onChange({...form, monthly_budget_dn: e.target.value})}
              placeholder="0.00"
              disabled={!isEnabled}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Split equally across active D+N offsets in the month. Set to 0 to disable D+N payments.
            </p>
          </div>
          <div className="rounded-md bg-surface dark:bg-gray-870 px-3 py-2 text-xs text-gray-700 dark:text-gray-300 flex items-center justify-between">
            <span>Total monthly budget</span>
            <span className="font-semibold">
              €{(
                (parseFloat(form.monthly_budget_intraday) || 0) +
                (parseFloat(form.monthly_budget_d1) || 0) +
                (parseFloat(form.monthly_budget_dn) || 0)
              ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Min payout threshold (€)
            </label>
            <FormInput
              size="medium"
              type="number"
              step="0.01"
              min="0"
              value={form.min_payout_threshold}
              onChange={e => onChange({...form, min_payout_threshold: e.target.value})}
              placeholder="1000.00"
              disabled={!isEnabled}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Below this monthly total per forecaster, unpaid balance is carried over to the next month. Set to 0 to disable.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">{form.is_active ? 'Active' : 'Inactive'}</label>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Toggle to activate or deactivate this resource</p>
          </div>
          <button
            type="button"
            onClick={() => onChange({...form, is_active: !form.is_active})}
            disabled={!isEnabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
              form.is_active ? 'bg-brand-600' : 'bg-gray-300 dark:bg-gray-600'
            } ${!isEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <button
            type="submit"
            disabled={!isEnabled}
            className={`flex-1 px-4 py-2 rounded-md border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 text-sm font-medium transition-colors ${
              isEnabled ? 'hover:bg-brand-100 dark:hover:bg-brand-900/50' : 'cursor-not-allowed opacity-50'
            }`}
          >
            {editing ? 'Update' : 'Create'}
          </button>
          {editing && (
            <button
              type="button"
              onClick={onCancel}
              disabled={!isEnabled}
              className={`px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-sm font-medium text-gray-700 dark:text-gray-200 transition-colors ${
                isEnabled ? 'hover:bg-gray-50 dark:hover:bg-gray-600' : 'opacity-50 cursor-not-allowed'
              }`}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
